import { Box, Divider } from '@hopskipdrive/react-components';

import { updateLocation } from '../../../api/services/Locations';
import { DROPOFF, PICKUP } from '../../../constants';
import { formatLocations } from '../../../hooks/useLocationOptions';
import { LocationAccordion } from '../../components/LocationAccordion';
import { RiderAccordion } from '../../components/RiderAccordion';
import { useRideEditor } from '../../hooks/useRideEditor';

import { helpers } from './helpers';
import { RiderAssistantWaypoint } from './RiderAssistantWaypoint';
import {
  ArrivalEvent,
  CareDriverCheckedInEvent,
  CareDriverStartedEvent,
  CompletedRideEvent,
  DepartureEvent,
} from './WaypointEventLog';
import { RiderAssistant, Timeline } from './Waypoints.styles';

export const WaypointsView = ({ rideDetails, isReadOnly }: { rideDetails: RideDetails; isReadOnly: boolean }) => {
  const { getRiderOptions, isFetching } = useRideEditor();

  const { ridersOnRide, updateRiderNotes } = getRiderOptions();
  const rideType = rideDetails?.waypoints?.[0].anchor ? PICKUP : DROPOFF;
  const waypoints = rideDetails.waypoints;
  const updateLocationDetails = async (data) => updateLocation(data.id, data);

  return (
    <Timeline>
      <CareDriverCheckedInEvent driver={rideDetails.driver} checkedInAt={rideDetails.checkedInAt} />
      <CareDriverStartedEvent driver={rideDetails.driver} startedAt={rideDetails.startedAt} />
      {waypoints?.map((waypoint, index) => {
        if (waypoint.riderAssistants?.length > 0) {
          return (
            <RiderAssistantWaypoint
              key={waypoint.id}
              num={index + 1}
              rideLabel={helpers[rideType].getRALabel()}
              location={waypoints.find((item) => item.location?.placeId === waypoint.location.placeId)?.location}
              type={rideType}
              riderAssistants={waypoint.riderAssistants}
            />
          );
        }

        return (
          <Timeline key={waypoint.id}>
            <Timeline.Item
              dotContent={
                helpers[rideType].getWaypointType(index, waypoints.length) === DROPOFF ? (
                  <Timeline.NumberDot num={index + 1} isComplete={!!waypoint?.actualArrivesAt} />
                ) : (
                  <Timeline.LocationDot num={index + 1} isComplete={!!waypoint?.actualArrivesAt} />
                )
              }
              connector={
                <Timeline.SolidConnector
                  $strokeColor={waypoint?.actualArrivesAt || rideDetails?.completedAt ? '#40EBAD' : ''}
                />
              }
            >
              <div>
                <Box mb={5} display="flex" flexDirection="row" justifyContent="space-between">
                  <ArrivalEvent
                    waypoint={waypoint}
                    waypointIndex={index}
                    waypointType={helpers[rideType].getWaypointType(index, waypoints.length)}
                    driver={rideDetails.driver}
                    estimatedStartsAt={rideDetails?.estimatedStartsAt}
                    timelinessStatus={rideDetails?.timelinessStatus}
                    startsAt={rideDetails?.startsAt}
                  />
                </Box>
                <Box mb={5} pb={0} sx={{ borderBottom: '1px solid #DFE4E8' }}></Box>
                {ridersOnRide.length === 0 && isFetching ? (
                  <Box mb={4}>
                    <RiderAccordion
                      isFetching={isFetching}
                      rider={undefined}
                      waypointType={helpers[rideType].getWaypointType(index, waypoints.length)}
                      locationId={waypoint?.location?.id}
                      updateRiderNotes={updateRiderNotes}
                      disableRemove={true}
                      onRemove={() => null}
                      isReadOnly={isReadOnly}
                    />
                  </Box>
                ) : null}
                {ridersOnRide
                  .filter((rider) => waypoint.anchor || waypoint.riders.find((r) => r.slug === rider.id))
                  ?.map((selectedRider) => (
                    <Box mb={4} key={`waypointsAttributes-${waypoint.id}-rider-${selectedRider.id}`}>
                      <RiderAccordion
                        rider={selectedRider}
                        waypointType={helpers[rideType].getWaypointType(index, waypoints.length)}
                        locationId={waypoint?.location?.id}
                        updateRiderNotes={updateRiderNotes}
                        disableRemove={true}
                        onRemove={() => null}
                        isReadOnly={isReadOnly}
                      />
                    </Box>
                  ))}
                {helpers[rideType].getWaypointType(index, waypoints.length) === 'pick_up' &&
                  index === 0 &&
                  waypoints[waypoints.length - 1]?.riderAssistants?.length > 0 &&
                  waypoints[waypoints.length - 1]?.riderAssistants?.map((ra) => (
                    <RiderAssistant key={ra.firstName + ra.lastName} data-testid="ra-rider">
                      <span>RA</span>
                      <div>
                        {ra.firstName} {ra.lastName}
                      </div>
                    </RiderAssistant>
                  ))}

                {helpers[rideType].getWaypointType(index, waypoints.length) === 'drop_off' &&
                  index === waypoints.length - 1 &&
                  waypoints[0].riderAssistants?.length > 0 &&
                  waypoints[0].riderAssistants?.map((ra) => (
                    <RiderAssistant key={ra.firstName + ra.lastName} data-testid="ra-rider">
                      <span>RA</span>
                      <div>
                        {ra.firstName} {ra.lastName}
                      </div>
                    </RiderAssistant>
                  ))}

                <Box pb={0} mb={5} sx={{ borderBottom: '1px dashed #DFE4E8' }}></Box>
                <LocationAccordion
                  isFetching={isFetching}
                  editMode={false}
                  location={waypoint.location}
                  waypointType={helpers[rideType].getWaypointType(index, waypoints.length)}
                  locations={formatLocations([waypoint.location])}
                  onRemove={() => null}
                  updateLocation={updateLocationDetails}
                  isReadOnly={isReadOnly}
                />

                <Divider bgColor="#F0F5FA" opacity={0.5} mt={5} mb={5} />
              </div>
            </Timeline.Item>
            {waypoints[index + 1] && rideDetails.waypoints[index + 1] && (
              <DepartureEvent
                waypoint={waypoint}
                waypointType={helpers[rideType].getWaypointType(index + 1, waypoints.length).replace('_', '-')}
                driver={rideDetails.driver}
              />
            )}
            {index == waypoints.length - 1 && (
              <CompletedRideEvent
                driver={rideDetails.driver}
                completedAt={rideDetails.completedAt || null}
                endsAt={rideDetails.endsAt}
              />
            )}
          </Timeline>
        );
      })}
    </Timeline>
  );
};
